import PropTypes from 'prop-types';
import React, { Fragment, useContext, useEffect } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { ArchiveSpacePortal } from '@confluence/archive-space';
import { DeleteSpacePortal } from '@confluence/delete-space';
import { LazySpaceSettingsForgeExtensionPortal } from '@confluence/space-settings-forge-extension';
import { CopySpacePermissionsForExistingSpacePortal } from '@confluence/copy-space-permissions';
import { BulkSpacePermissionsPortal } from '@confluence/bulk-permissions/entry-points/BulkPermissions';
import { DefaultSpacePermissionsTabPortal } from '@confluence/default-space-permissions/entry-points/DefaultSpacePermissionsTab';
import { useSpaceId } from '@confluence/space-utils';
import { SpaceSettingsNavigationV2Portal } from '@confluence/space-settings';
import { SpaceTrashPortal } from '@confluence/space-trash';
import {
	SiteSettingsHelpPanelLink,
	BackupManagerButton,
	getRouteData,
} from '@confluence/in-product-help';
import { GuestBannerSpacePermissionsPortal } from '@confluence/external-collab-ui/entry-points/GuestBannerSpacePermissionsPortal';
import { PageStateContainer } from '@confluence/page-context';

const i18n = defineMessages({
	importingTemplates: {
		id: 'standalone-navigation.importing.templates',
		defaultMessage: 'More about importing templates',
		description: 'Learn more link about importing templates',
	},
});

export const PortalsForLegacyPages = ({ spaceKey, isSpaceSettingsScreen }) => {
	const {
		location: { pathname },
	} = useContext(RoutesContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const spaceId = useSpaceId(spaceKey);

	useEffect(() => {
		const { section, screen } = getRouteData(pathname);

		// We only want analytics for IPH specific routes for now
		if (screen !== 'notTracked') {
			const isSpacePermissions = pathname.includes('/spaces/spacepermissions.action');
			const spacePermissionSpecificAttributes = isSpacePermissions ? { containerId: spaceId } : {};

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: screen,
					attributes: {
						section,
						...spacePermissionSpecificAttributes,
					},
				},
			}).fire();
		}
	}, [pathname, createAnalyticsEvent, spaceId]);

	return (
		pathname && (
			<Fragment>
				{/* SpaceSettingsNavigationV2Portal renders on all legacy space settings pages
            since it encapsulates the space settings navigation */}
				{isSpaceSettingsScreen ? (
					<PageStateContainer spaceKey={spaceKey}>
						<SpaceSettingsNavigationV2Portal spaceKey={spaceKey} />
					</PageStateContainer>
				) : null}
				{pathname.includes('/spaces/spacepermissions.action') && (
					<Fragment>
						<CopySpacePermissionsForExistingSpacePortal analyticsEventSourceName="viewSpacePerms" />
						<GuestBannerSpacePermissionsPortal />
					</Fragment>
				)}
				{pathname.includes('/spaces/archivespace.action') && (
					<ArchiveSpacePortal spaceKey={spaceKey} pathname={pathname} />
				)}
				{pathname.includes('/spaces/removespace.action') && (
					<DeleteSpacePortal spaceKey={spaceKey} pathname={pathname} />
				)}
				{pathname.includes('/admin/permissions/viewdefaultspacepermissions.action') && [
					<BulkSpacePermissionsPortal key="bulk-permissions-portal" />,
					<DefaultSpacePermissionsTabPortal key="default-space-permissions-portal" />,
				]}
				{pathname.includes('/pages/viewtrash.action') && <SpaceTrashPortal spaceKey={spaceKey} />}
				{pathname.includes('/templatePackage/configurePlugin.action') && (
					<SiteSettingsHelpPanelLink
						articleId="7p0QTJJI0VkoU49M4GzKH2"
						articleTopic="admin-import-templates"
						portalTargetId="site-settings-import"
						buttonContent={<FormattedMessage {...i18n.importingTemplates} />}
						pathname={pathname}
						routeGroup="site-settings"
						routeName="undefined"
					/>
				)}
				{pathname.includes('/plugins/servlet/ondemandbackupmanager') && (
					<BackupManagerButton pathname={pathname} />
				)}
				{/* LazySpaceSettingsForgeExtensionPortal shows up in Integrations tab and can have many extensions and many urls */}
				<LazySpaceSettingsForgeExtensionPortal spaceKey={spaceKey} />
			</Fragment>
		)
	);
};

// needed for react-proptypes warning
PortalsForLegacyPages.propTypes = {
	spaceKey: PropTypes.string,
	isSpaceSettingsScreen: PropTypes.bool,
};
